import React from 'react';
import { shapeOf, formsFor, positionAt, POSITION_LABELS, isJoiner } from './letterForms';
import { pickExample } from './letterExamples';
import { audioUrlFor, playLoud } from './audioStorage';
import WordInfoButton from './WordInfo';

// One letter taught in the teach phase: name + sound, its positional shapes,
// and a real example word (with audio + the letter highlighted in place).
function LetterCard({ letter, recordedSet }) {
  const forms = formsFor(letter.dari);

  return (
    <div className='letter-card'>
      <div className='letter-head'>
        <span className='letter-glyph' lang='fa' dir='rtl'>{letter.dari}</span>
        <div className='letter-id'>
          <div className='letter-name'>{letter.english}</div>
          <div className='letter-sound'>sounds like “{letter.sound}”</div>
          {!isJoiner(letter.dari) && (
            <div className='letter-flag'>Does not join the next letter</div>
          )}
        </div>
      </div>

      <div className='forms-row'>
        {forms.map((pos) => (
          <ExampleForm key={pos} letter={letter} position={pos} recordedSet={recordedSet} />
        ))}
      </div>
    </div>
  );
}

function ExampleForm({ letter, position, recordedSet }) {
  // Only use a word that genuinely shows the letter in THIS position; otherwise
  // the standalone shape above still teaches the form and we say so plainly.
  const word = pickExample(letter.dari, position, recordedSet);
  let targetIndex = -1;
  if (word) {
    targetIndex = Array.from(word).findIndex(
      (ch, i) => ch === letter.dari && positionAt(word, i) === position
    );
  }

  return (
    <div className='form-cell'>
      <div className='form-label'>{POSITION_LABELS[position]}</div>
      <div className='form-shape' lang='fa' dir='rtl'>{shapeOf(letter.dari, position)}</div>
      {word
        ? <ExampleWord word={word} target={letter.dari} targetIndex={targetIndex} recordedSet={recordedSet} info />
        : <div className='form-example empty'>no example yet</div>}
    </div>
  );
}

// Shows the example word with the target letter highlighted, plus a 🔊 button
// (the whole-word recording) when one exists.
// `targetIndex` (optional) highlights one specific occurrence — needed because
// a word can contain the same letter in several positions. When omitted (e.g.
// the quiz "spot the letter" question), every occurrence is highlighted.
export function ExampleWord({ word, target, targetIndex, recordedSet, subtle, info }) {
  const letters = Array.from(word);
  const recorded = recordedSet && recordedSet.has(word);
  const isHighlighted = (ch, i) =>
    targetIndex != null && targetIndex >= 0 ? i === targetIndex : ch === target;

  const hasIcons = recorded || info;

  return (
    <div className={`form-example${subtle ? ' subtle' : ''}`}>
      <span className='example-word' lang='fa' dir='rtl'>
        {letters.map((ch, i) => (
          <span key={i} className={isHighlighted(ch, i) ? 'hl' : ''}>{ch}</span>
        ))}
      </span>
      {hasIcons && (
        <span className='example-icons'>
          {recorded && (
            <button
              type='button'
              className='listen-icon-btn'
              onClick={() => playLoud(audioUrlFor(word))}
              aria-label='Listen'
              title='Listen'
            >
              🔊
            </button>
          )}
          {info && <WordInfoButton word={word} />}
        </span>
      )}
    </div>
  );
}

// Re-export so quizzes can reuse position detection without another import.
export { positionAt };
export default LetterCard;
